import { useState, useEffect, useCallback, useRef } from "react";
import { Route, ContainerPoint } from "../types";
import { RouteService } from "../services/routeService";

const ROUTES_STORAGE_KEY = "mapMonitoring_routes";

function dedupeById(items: Route[]): Route[] {
    const map = new Map<string, Route>();
    for (const r of items) {
        if (!r?.id) continue;
        map.set(r.id, r); // последний выигрывает
    }
    return Array.from(map.values());
}

function sanitizeActiveIds(activeIds: string[], routes: Route[]): string[] {
    const ids = new Set(routes.map((r) => r.id));
    return activeIds.filter((id) => ids.has(id));
}

export function useRoutes() {
    const loadRoutesFromStorage = (): Route[] => {
        try {
            const stored = localStorage.getItem(ROUTES_STORAGE_KEY);
            if (!stored) return [];
            const parsed = JSON.parse(stored) as Route[];
            return dedupeById(Array.isArray(parsed) ? parsed : []);
        } catch (error) {
            console.error("Ошибка при загрузке маршрутов из LocalStorage:", error);
            return [];
        }
    };

    const [routes, setRoutes] = useState<Route[]>(loadRoutesFromStorage);
    const [activeRouteIds, setActiveRouteIds] = useState<string[]>([]);

    // refs для стабильных коллбэков, чтобы читать актуальное состояние
    const routesRef = useRef<Route[]>(routes);
    const activeIdsRef = useRef<string[]>(activeRouteIds);

    useEffect(() => {
        routesRef.current = routes;
    }, [routes]);

    useEffect(() => {
        activeIdsRef.current = activeRouteIds;
    }, [activeRouteIds]);

    // ✅ сохраняем дедупнутый список
    useEffect(() => {
        try {
            const normalized = dedupeById(routes);
            localStorage.setItem(ROUTES_STORAGE_KEY, JSON.stringify(normalized));
        } catch (error) {
            console.error("Ошибка при сохранении маршрутов в LocalStorage:", error);
        }
    }, [routes]);

    // ✅ чистим активные id от удалённых маршрутов
    useEffect(() => {
        setActiveRouteIds((prev) => sanitizeActiveIds(prev, routes));
    }, [routes]);

    const addRoute = useCallback((route: Route) => {
        if (!RouteService.validateRoute(route)) {
            console.error("Некорректный маршрут:", route);
            return;
        }

        setRoutes((prev) => {
            // ✅ если id уже существует — обновляем (upsert)
            const idx = prev.findIndex((r) => r.id === route.id);
            if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = {
                    ...copy[idx],
                    ...route,
                    updatedAt: new Date().toISOString(),
                };
                return dedupeById(copy);
            }

            return dedupeById([
                ...prev,
                {
                    ...route,
                    createdAt: route.createdAt ?? new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                },
            ]);
        });
    }, []);

    const updateRoute = useCallback((routeId: string, updates: Partial<Route>) => {
        setRoutes((prev) =>
            dedupeById(
                prev.map((route) =>
                    route.id === routeId
                        ? { ...route, ...updates, updatedAt: new Date().toISOString() }
                        : route
                )
            )
        );
    }, []);

    const removeRoute = useCallback((routeId: string) => {
        setRoutes((prev) => prev.filter((route) => route.id !== routeId));
        setActiveRouteIds((prev) => prev.filter((id) => id !== routeId));
    }, []);

    const getRoute = useCallback((routeId: string): Route | undefined => {
        return routes.find((route) => route.id === routeId);
    }, [routes]);

    const getVisibleRoutes = useCallback((): Route[] => {
        // ВАЖНО: используем `routes`, иначе при setRoutes() будет лаг в 1 рендер,
        // потому что routesRef обновляется только в useEffect.
        return routes.filter((route) => route.visible !== false);
    }, [routes]);

    const getActiveRoutes = useCallback((): Route[] => {
        const ids = new Set(activeRouteIds);
        return routes.filter((route) => ids.has(route.id));
    }, [routes, activeRouteIds]);

    const setActiveRoutes = useCallback((routeIds: string[]) => {
        setActiveRouteIds((prev) => {
            // ✅ убираем дубли + фильтруем по существующим маршрутам
            const unique = Array.from(new Set(routeIds));
            return sanitizeActiveIds(unique, routesRef.current);
        });
    }, []);

    const toggleRouteVisibility = useCallback((routeId: string) => {
        setRoutes((prev) =>
            dedupeById(
                prev.map((route) =>
                    route.id === routeId
                        ? {
                            ...route,
                            visible: route.visible === false, // если был false -> true, иначе -> false
                            updatedAt: new Date().toISOString(),
                        }
                        : route
                )
            )
        );
    }, []);

    const createRouteFromCoordinates = useCallback(
        (
            id: string,
            name: string,
            points: [number, number][],
            style?: Parameters<typeof RouteService.createRoute>[3]
        ) => {
            const route = RouteService.createRoute(id, name, points, style);
            addRoute(route); // ✅ upsert защищает от дублей
            return route;
        },
        [addRoute]
    );

    /**
     * Создает маршрут из массива КП (контейнерных площадок)
     * Координаты используются в последовательности от первой до последней
     */
    const createRouteFromContainerPoints = useCallback(
        (
            id: string,
            name: string,
            containerPoints: ContainerPoint[],
            style?: Parameters<typeof RouteService.createRouteFromContainerPoints>[3]
        ) => {
            try {
                const route = RouteService.createRouteFromContainerPoints(id, name, containerPoints, style);
                addRoute(route);
                return route;
            } catch (error) {
                console.error("Ошибка при создании маршрута из КП:", error);
                return null;
            }
        },
        [addRoute]
    );

    const clearRoutes = useCallback(() => {
        setRoutes([]);
        setActiveRouteIds([]);
        try {
            localStorage.removeItem(ROUTES_STORAGE_KEY);
        } catch {
            // ignore
        }
    }, []);

    /** Заменяет только маршруты с plannedMeta; локальные/КП остаются. */
    const setPlannedRoutes = useCallback((planned: Route[]) => {
        setRoutes((prev) => {
            const kept = prev.filter((r) => !r.plannedMeta);
            return dedupeById([...kept, ...planned]);
        });
    }, []);

    /**
     * Чистит только planned-маршруты (те, что пришли с API планирования).
     * Нужен, чтобы временные preview-данные не "прилипали" из LocalStorage.
     */
    const clearPlannedRoutes = useCallback(() => {
        const kept = routesRef.current.filter((r) => !r.plannedMeta);
        setRoutes(kept);
        setActiveRouteIds((prev) => sanitizeActiveIds(prev, kept));
        try {
            localStorage.setItem(ROUTES_STORAGE_KEY, JSON.stringify(dedupeById(kept)));
        } catch {
            // ignore
        }
    }, []);

    return {
        routes,
        activeRouteIds,
        addRoute,
        updateRoute,
        removeRoute,
        getRoute,
        getVisibleRoutes,
        getActiveRoutes,
        setActiveRoutes,
        toggleRouteVisibility,
        createRouteFromCoordinates,
        createRouteFromContainerPoints,
        clearRoutes,
        setPlannedRoutes,
        clearPlannedRoutes,
    };
}
